
import React, { useContext, useMemo, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import { Badge, Button, Dialog, DialogActions, DialogContent, IconButton, Tab, Tabs } from '@mui/material';
import { AccountContext } from '../context/account';
import styles from "../styles/components/purchase-request-modal.module.scss";
import tableStyles from "../styles/components/table.module.scss";
import { ModalTitle } from './modal-title';

import classNames from "classnames";
import { format } from "date-fns";
import { nanoid } from 'nanoid';
import { addCartItem } from "../api/cart";
import { rejectRequestItem, updateRequestItem } from "../api/orders";
import { MapDataContext } from '../context/map-data';
import { OrderRequestItem } from '../core/global';
import { roleOptions } from '../data/form-shapes';
import { applyTimeFrom, getItemByKeyValue, getPurchaseRequestsByType } from '../helpers/core';
import { CopyButton } from './copy-button';

export const getBadgeColor = (status: string) => {
  switch (status) {
    case 'approved': return 'success'
    case 'rejected': return 'error'
    case 'processing': return 'primary'
    case 'revoked': return 'error'
    default: return 'info'
  }
}

interface PurchaseRequestModalProps {
  onClose: () => void
}

export const PurchaseRequestModal = ({
  onClose,
}: PurchaseRequestModalProps) => {
  const [tabIndex, setTabIndex] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const {
    companyPurchaseRequests,
    account,
    showApprovePurchaseModal
  } = useContext(AccountContext);
  
  const allCompanyActiveRequests = useMemo(() => {
    return companyPurchaseRequests && showApprovePurchaseModal ? getPurchaseRequestsByType(companyPurchaseRequests, Number(showApprovePurchaseModal)) : []
  }, [companyPurchaseRequests, showApprovePurchaseModal])

  const activeRequests = useMemo(() => {
    return getPurchaseRequestsByType(companyPurchaseRequests, Number(showApprovePurchaseModal), ['pending', 'processing'])
  }, [allCompanyActiveRequests])

  const processedRequests = useMemo(() => {
    return getPurchaseRequestsByType(companyPurchaseRequests, Number(showApprovePurchaseModal), ['approved', 'rejected', 'revoked'])
  }, [allCompanyActiveRequests])

  const currentCompany = useMemo(() => {
    if (account && account.organisation) {
      const found = account.organisation.filter(({id}) => Number(id) === Number(showApprovePurchaseModal))
      return found.length > 0 ? found[0] : null
    }
    return null
  }, [account, showApprovePurchaseModal])

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={"lg"}
      fullWidth
    >
      <ModalTitle onClose={onClose}>Location data point purchase request {currentCompany ? `for ${currentCompany.name}` : ''}</ModalTitle>
      <DialogContent>
        <p>As an administrator for <strong>{currentCompany ? `${currentCompany.name}` : ''}</strong>, you can approve or reject data purchase requests submitted by other members of the account. When you approve a purchase request, this will be added to the shopping cart and will need to be processed.</p>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile
        >
          {['Active requests', 'Processed requests'].map( (name, index) => {
            const counter = index === 0 ? activeRequests?.length : processedRequests?.length
            return (<Tab key={nanoid()} label={<Badge
              badgeContent={(counter && counter > 0) ? counter : null}
              color='error'
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >{name}</Badge>} />)
          })}
        </Tabs>
        {tabIndex === 0 && <PurchaseRequestsTable
            data={activeRequests}
          />
        }
        {tabIndex === 1 && <PurchaseRequestsTable
            data={processedRequests}
          />
        }
      </DialogContent>
      <>
      <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
          <div />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    </Dialog>

  )
}

export interface TableProps {
  data?: OrderRequestItem[] | null
}

export const PurchaseRequestsTable = ({
  data
}: TableProps ) => {
  const [showTokenModal, setShowTokenModal] = useState(false)
  const [editedRowId, setEditedRowId] = useState<number | string | null>(null)
  const [deletingItem, setDeletingItem] = useState<number | string | null>(null)

  const {
    refetchCart,
    setShowCart,
    refetchCompanyRequests,
  } = useContext(AccountContext);

  const {
    dataSources
  } = useContext(MapDataContext);


  if (!data || data.length === 0) {
    return <>
      <br />
      <p>You don't have any active purchase requests.</p>
    </>
  }
  return (
    <>
      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th className={tableStyles.fieldName} style={{minWidth: '300px'}}>
              Request
            </th>
            <th>Date range</th>
            <th className={tableStyles.fieldName}>
              Company
            </th>
            <th className={tableStyles.actions}>Status</th>
          </tr>
        </thead>
        <tbody>
          {data && data.map(item => {
              if (item) {
                const {
                  id,
                  email,
                  first_name,
                  last_name,
                  created_at,
                  start_date: startDate,
                  end_date: endDate,
                  model,
                  provider,
                  geography,
                  version,
                  lat,
                  lon,
                  notes,
                  organisation_name,
                  organisation_id,
                  status,
                  role,
                } = item
                
                const modelKey = `${provider}-${model}-${geography}-${version}`
                let userRole = ''
                const found = getItemByKeyValue(roleOptions, 'key', role)
                if (found !== -1) {
                  const {value} = found
                  userRole = value
                }
                const canEditRow = !(status === 'approved' || status === 'rejected' || status === 'revoked')
                const isEdit = editedRowId !== null && editedRowId === id
                const range = `${format(new Date(startDate), 'MM/yyyy')} - ${format(new Date(endDate), 'MM/yyyy')}`
                return (
                  <React.Fragment key={id}>
                    <tr className={classNames({
                      [tableStyles.deleting]: isEdit,
                      [tableStyles.noBorder]: !!notes,
                    })}>
                      {/* <th>{lat}, {lon}</th> */}
                      <td>
                        <strong>{first_name} {last_name} {userRole ? ` (${userRole})`: ''}</strong><br />
                        <strong>{email}</strong><CopyButton content={email} /><br />

                        <strong>{lat}, {lon}</strong><br />
                        {dataSources && dataSources[modelKey].name}
                        <i style={{fontSize: "12px", display: "block", lineHeight: "16px"}}>Requested on: {format(new Date(created_at), 'dd/MM/yyyy')}</i>
                      </td>
                      <td className={tableStyles.date} style={{maxWidth: "100px"}}>{range}</td>
                      <td className={tableStyles.item}>{organisation_name}</td>
                      <td className={tableStyles.actions}>
                        <span className={styles.badge}>
                          <Badge
                            badgeContent={status}
                            color={getBadgeColor(status)}
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                          ></Badge>
                        </span>
                        {canEditRow && 
                          <>
                            {!isEdit ?
                              <>
                                <IconButton
                                  size='small'
                                  onClick={() => {
                                    setEditedRowId(id)
                                  }
                                }>
                                  <DeleteIcon />
                                </IconButton>
                                <IconButton
                                  size='small'
                                  onClick={async (e: React.SyntheticEvent) => {
                                    e.preventDefault()
                                    if (startDate && endDate && dataSources && dataSources[modelKey]) {
                                      const minDate = dataSources[modelKey].start_date || null
                                      const maxDate = dataSources[modelKey].end_date || null
                                      if (minDate && maxDate) {
                                        const {
                                          project_id, 
                                          id, 
                                          created_at, 
                                          organisation_id, 
                                          requested_by_uid, 
                                          first_name, 
                                          last_name, 
                                          organisation_name,
                                          params,
                                        } = item
                                        
                                        const start_date = applyTimeFrom(new Date(startDate), new Date(minDate));
                                        const end_date = applyTimeFrom(new Date(endDate), new Date(maxDate));
                                        const paramsConfig = typeof params !== "undefined" && params !== null && params.config ? params.config : null
                                        
                                        const purchaseItem = {
                                          lat,
                                          lon,
                                          model,
                                          provider,
                                          geography,
                                          version,
                                          start_date,
                                          end_date,
                                          project_id: null,
                                          params: {
                                            id,
                                            project_id,
                                            organisation_id,
                                            requested_by_uid,
                                            created_at,
                                            first_name: first_name || email, 
                                            last_name, 
                                            organisation_name,
                                            config: paramsConfig,
                                          }
                                        }
                                        await addCartItem({...purchaseItem})
                                        refetchCart()
                                        setShowCart(true)
                                        await updateRequestItem({
                                          id,
                                          status: 'processing',
                                          company_id: organisation_id
                                        });
                                        refetchCompanyRequests();
                                      }
                                    }
                                  }
                                }>
                                  <ShoppingCartIcon />
                                </IconButton>
                              </>
                              :
                              <div>
                                <Button 
                                  size='small'
                                  color='error'
                                  startIcon={<DeleteForeverIcon />}
                                  onClick={async () => {
                                    setDeletingItem(id)
                                    await rejectRequestItem({
                                      order_item_id: id,
                                    });
                                    refetchCompanyRequests();
                                    refetchCart()
                                    setDeletingItem(null)
                                    setEditedRowId(null)
                                  }}>
                                  Remove
                                </Button>
                                <Button 
                                  size='small'
                                  color="success"
                                  onClick={() => {
                                    setEditedRowId(null)
                                  }}>
                                  Keep
                                </Button>
                              </div>
                            }
                          </>
                        }
                      </td>
                    </tr>
                    {notes && <tr>
                      <td colSpan={4}><p><strong>Message from {first_name}:</strong> {notes}</p></td>
                    </tr>}
                  </React.Fragment>
                )  
              }
              return null
            })
          }
        </tbody>
      </table>
    </>
  )
}

